import React, { useEffect, useState, useRef } from 'react';
import { HeroContent } from './HeroContent';
import { VideoDemo } from './VideoDemo';
import { HeroGradient } from './HeroGradient';
import { CurvedArrow } from './CurvedArrow';
import { DemoModal } from './DemoModal';
import { RoleChips } from './RoleChips';
import { trackSectionView } from '../../firebaseConfig';
import { CompanyLogos } from '../CompanyLogos/CompanyLogos';

export function Hero() {
  const [isDemoOpen, setIsDemoOpen] = useState(false);
  const [animationPhase, setAnimationPhase] = useState(0); // 0: initial, 1: gradient, 2: content, 3: complete
  const heroRef = useRef<HTMLDivElement>(null);
  const [isDark, setIsDark] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect theme change and initialize
  useEffect(() => {
    // Check for dark mode
    const checkDarkMode = () => {
      const isDarkMode = document.documentElement.classList.contains('dark');
      setIsDark(isDarkMode);
    };

    // Check for mobile screen size
    const checkMobileScreen = () => {
      setIsMobile(window.innerWidth < 768); // Typically 768px is the mobile breakpoint
    };

    // Initial check
    checkDarkMode();
    checkMobileScreen();

    // Set up listeners for changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.attributeName === 'class' &&
          mutation.target === document.documentElement
        ) {
          checkDarkMode();
        }
      });
    });

    // Start observing document for class changes
    observer.observe(document.documentElement, { attributes: true });

    // Listen for window resize
    window.addEventListener('resize', checkMobileScreen);

  }, []);

  // Track section view when Hero component mounts
  useEffect(() => {
    trackSectionView('Hero Section');
    
    // Start animation sequence after navbar animation completes
    setTimeout(() => setAnimationPhase(1), 1600); // Start with gradient
    setTimeout(() => setAnimationPhase(2), 2200); // Then content
    setTimeout(() => setAnimationPhase(3), 2800); // Complete animation
    
    // Add smooth scrolling effect to the entire page
    document.documentElement.style.scrollBehavior = 'smooth';
    
    // Setup premium scroll effects
    const setupScrollEffects = () => {
      if (!heroRef.current) return;
      
      // Parallax effect for elements with the parallax-element class
      const handleScroll = () => {
        const scrollPosition = window.scrollY;
        const parallaxElements = document.querySelectorAll('.parallax-element');
        const blurElements = document.querySelectorAll('.blur-on-scroll');
        const scaleElements = document.querySelectorAll('.scale-on-scroll');
        
        // Apply parallax effect
        parallaxElements.forEach((element) => {
          const speed = Number(element.getAttribute('data-speed') || 0.1);
          const yPos = -scrollPosition * speed;
          (element as HTMLElement).style.transform = `translateY(${yPos}px)`;
        });
        
        // Apply blur effect for depth
        blurElements.forEach((element) => {
          const scrollPercentage = Math.min(1, scrollPosition / window.innerHeight);
          // const blurAmount = 5 * scrollPercentage;
          const blurAmount = 0;
          (element as HTMLElement).style.filter = `blur(${blurAmount}px)`;
          // (element as HTMLElement).style.opacity = `${1 - scrollPercentage * 0.5}`;
        });
        
        // Apply scale effect for 3D feel
        scaleElements.forEach((element) => {
          const scrollPercentage = Math.min(1, scrollPosition / window.innerHeight);
            const scaleAmount = 1 - scrollPercentage * 0.1;
            (element as HTMLElement).style.transform = `scale(${scaleAmount})`;
        });
      };
      
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    };
    
    const effectsTimeout = setTimeout(setupScrollEffects, 3500);
    return () => clearTimeout(effectsTimeout);
  }, []);

  return (
    <>
      <header ref={heroRef} className="relative pt-64 md:pt-72 pb-16 md:py-36 overflow-hidden">
        {/* Subtle gradient background - reduced brightness by 4-5% */}
        <div 
          className={`
            absolute inset-0 transition-opacity duration-1000 ease-out
            ${animationPhase >= 1 ? 'opacity-95' : 'opacity-0'}
          `}
        >
          <HeroGradient />
          
          {/* Subtle background elements with reduced opacity */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_10%_20%,rgba(37,99,235,0.05),transparent_35%)] dark:bg-[radial-gradient(circle_at_10%_20%,rgba(59,130,246,0.05),transparent_35%)] parallax-element" data-speed="0.05"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_70%,rgba(16,185,129,0.05),transparent_35%)] dark:bg-[radial-gradient(circle_at_80%_70%,rgba(52,211,153,0.05),transparent_35%)] parallax-element" data-speed="0.08"></div>
          
          {/* Floating particles with reduced opacity */}
          <div className="absolute left-[10%] top-[15%] w-6 h-6 rounded-full bg-primary-light/15 dark:bg-primary-dark/15 blur-md animate-float-slow parallax-element" data-speed="0.12" style={{ animationDelay: '0s', animationDuration: '7s' }}></div>
          <div className="absolute left-[25%] top-[40%] w-4 h-4 rounded-full bg-secondary-light/15 dark:bg-secondary-dark/15 blur-md animate-float-slow parallax-element" data-speed="0.08" style={{ animationDelay: '1.5s', animationDuration: '5s' }}></div>
          <div className="absolute right-[15%] top-[20%] w-5 h-5 rounded-full bg-primary-light/15 dark:bg-primary-dark/15 blur-md animate-float-slow parallax-element" data-speed="0.16" style={{ animationDelay: '0.7s', animationDuration: '8s' }}></div>
          <div className="absolute right-[35%] top-[65%] w-3 h-3 rounded-full bg-secondary-light/15 dark:bg-secondary-dark/15 blur-md animate-float-slow parallax-element" data-speed="0.14" style={{ animationDelay: '2s', animationDuration: '6s' }}></div>
          
          {/* Diagonal subtle accent lines */}
          <div className="absolute -left-20 top-40 w-[500px] h-[1px] bg-gradient-to-r from-transparent via-primary-light/15 to-transparent rotate-[30deg] parallax-element" data-speed="0.1"></div>
          <div className="absolute -right-20 top-96 w-[500px] h-[1px] bg-gradient-to-r from-transparent via-secondary-light/15 to-transparent -rotate-[30deg] parallax-element" data-speed="0.1"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-7xl mx-auto">
            {/* Hero content with smooth fade-in and slide-up effect */}
            <div 
              className={`
                transform transition-all duration-1200 ease-[cubic-bezier(0.16,1,0.3,1)]
                ${animationPhase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}
              `}
            >
              <HeroContent onOpenDemo={() => setIsDemoOpen(true)} />
            </div>
            
            <div className="relative mt-24">
              {/* Video demo with smoother reveal animation */}
              <div 
                className={`
                  max-w-6xl mx-auto transform transition-all duration-1200 ease-[cubic-bezier(0.16,1,0.3,1)] scale-on-scroll
                  ${animationPhase >= 2 ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'}
                `}
                style={{ transitionDelay: '200ms' }}
              >
                <VideoDemo isDark={isDark} isMobile={isMobile} />
              </div>
              
              {/* Feature arrows with staggered entrance animation */}
              <CurvedArrow 
                className={`
                  hidden lg:block absolute -right-36 top-4 transform transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] blur-on-scroll blur-sm
                  ${animationPhase >= 3 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}
                `}
                text="Screen & Video Analysis"
                style={{ transitionDelay: '200ms' }}
              />
              <CurvedArrow 
                className={`
                  hidden lg:block absolute -right-36 top-1/3 transform transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] blur-on-scroll blur-sm
                  ${animationPhase >= 3 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}
                `}
                text="Constructive feedbacks"
                style={{ transitionDelay: '350ms' }}
              />
              <CurvedArrow 
                className={`
                  hidden lg:block absolute -right-36 top-2/3 transform transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] blur-on-scroll blur-sm
                  ${animationPhase >= 3 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}
                `}
                text="Transcription Records"
                style={{ transitionDelay: '500ms' }}
              />
              
              <CurvedArrow 
                className={`
                  hidden lg:block absolute -left-36 top-4 transform transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] blur-on-scroll blur-sm
                  ${animationPhase >= 3 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}
                `}
                text="AI-powered interviews"
                direction="right"
                style={{ transitionDelay: '200ms' }}
              />
              <CurvedArrow 
                className={`
                  hidden lg:block absolute -left-36 top-1/3 transform transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] blur-on-scroll blur-sm
                  ${animationPhase >= 3 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}
                `}
                text="Real interview experience"
                direction="right"
                style={{ transitionDelay: '350ms' }}
              />
              <CurvedArrow 
                className={`
                  hidden lg:block absolute -left-36 top-2/3 transform transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] blur-on-scroll blur-sm
                  ${animationPhase >= 3 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}
                `}
                text="Role-specific questions"
                direction="right"
                style={{ transitionDelay: '500ms' }}
              />
            </div>

            <div className="mt-12">
            <CompanyLogos />
            </div>

            {/* Role chips with smooth scaling animation */}
            <div 
              className={`
                transform transition-all duration-1200 ease-[cubic-bezier(0.16,1,0.3,1)]
                ${animationPhase >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}
              `}
              style={{ transitionDelay: '400ms' }}
            >
              <RoleChips />
            </div>
          </div>
        </div>
      </header>

      <DemoModal isOpen={isDemoOpen} onClose={() => setIsDemoOpen(false)} />
    </>
  );
}